/**
 * Global Chart.js configuration composable
 * Provides consistent visual styling across all charts in the application
 *
 * This composable handles:
 * - Font configurations (all fonts use Fira Sans)
 * - Legend styling (position, icon size, colors)
 * - Tooltip styling (colors, padding, fonts)
 * - Grid and axis styling (colors, borders)
 * - Dataset defaults (line/bar chart styling)
 *
 * Chart-specific logic (label formatters, dual axes, data transformations)
 * should remain in individual chart components.
 */

import type { ChartOptions } from 'chart.js';

// ============================================================================
// GLOBAL STYLING CONSTANTS
// ============================================================================

/**
 * Font family used across all chart text elements
 */
const CHART_FONT_FAMILY = 'Fira Sans, sans-serif';

/**
 * Color palette for chart UI elements
 */
const COLORS = {
  // Axis and legend text colors
  axisText: 'rgb(163, 163, 163)', // neutral-400
  legendText: 'rgb(115, 115, 115)', // neutral-500

  // Grid colors
  gridLine: 'rgba(0, 0, 0, 0.05)',

  // Tooltip colors
  tooltipBg: 'rgba(0, 0, 0, 0.85)',
  tooltipBorder: 'rgba(255, 255, 255, 0.1)',
  tooltipText: '#fff',
};

/**
 * Font size configurations for different chart elements
 */
const FONT_SIZES = {
  legend: 11,
  tooltip: 13,
  axisTick: 11,
};

// ============================================================================
// PRESET CONFIGURATIONS
// ============================================================================

/**
 * Dashboard-specific chart preset
 * - Top-right legend with small circular icons (6x6px)
 * - Compact padding and spacing
 * - Optimized for dashboard cards
 */
export function getDashboardChartConfig(): Partial<ChartOptions<any>> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          boxWidth: 6,
          boxHeight: 6,
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 8,
          font: {
            family: CHART_FONT_FAMILY,
            size: FONT_SIZES.legend,
          },
          color: COLORS.legendText,
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        position: 'nearest' as const,
        yAlign: 'bottom' as const,
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 6,
        boxHeight: 6,
        boxPadding: 4,
        pointBorderWidth: 0,
        backgroundColor: COLORS.tooltipBg,
        titleColor: COLORS.tooltipText,
        bodyColor: COLORS.tooltipText,
        borderWidth: 0,
        padding: 12,
        titleFont: {
          family: CHART_FONT_FAMILY,
          size: FONT_SIZES.tooltip,
          weight: 'normal',
        },
        bodyFont: {
          family: CHART_FONT_FAMILY,
          size: FONT_SIZES.tooltip,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: COLORS.axisText,
          font: {
            family: CHART_FONT_FAMILY,
            size: FONT_SIZES.axisTick,
            weight: 'bold',
          },
        },
        grid: {
          display: true,
          color: COLORS.gridLine,
          drawBorder: false,
        },
        border: {
          display: false,
        },
      },
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true, // Automatically skip labels to prevent overlap
          autoSkipPadding: 8, // Minimum 8px spacing between labels
          color: COLORS.axisText,
          font: {
            family: CHART_FONT_FAMILY,
            size: FONT_SIZES.axisTick,
            weight: 'bold',
          },
        },
        grid: {
          display: false,
          color: COLORS.gridLine,
          drawBorder: false,
        },
        border: {
          display: false,
        },
      },
    },
  };
}

/**
 * Base chart preset
 * - Bottom-center legend with standard icons
 * - Standard padding and spacing
 * - General purpose charts
 */
export function getBaseChartConfig(): Partial<ChartOptions<any>> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            family: CHART_FONT_FAMILY,
            size: FONT_SIZES.legend,
          },
          color: COLORS.legendText,
        },
      },
      tooltip: {
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 6,
        boxHeight: 6,
        pointBorderWidth: 0,
        backgroundColor: COLORS.tooltipBg,
        titleColor: COLORS.tooltipText,
        bodyColor: COLORS.tooltipText,
        borderWidth: 0,
        padding: 12,
        titleFont: {
          family: CHART_FONT_FAMILY,
          size: FONT_SIZES.tooltip,
          weight: 'normal',
        },
        bodyFont: {
          family: CHART_FONT_FAMILY,
          size: FONT_SIZES.tooltip,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: COLORS.axisText,
          font: {
            family: CHART_FONT_FAMILY,
            size: FONT_SIZES.axisTick,
            weight: 'bold',
          },
        },
        grid: {
          display: true,
          color: COLORS.gridLine,
          drawBorder: false,
        },
        border: {
          display: false,
        },
      },
      x: {
        ticks: {
          color: COLORS.axisText,
          font: {
            family: CHART_FONT_FAMILY,
            size: FONT_SIZES.axisTick,
          },
        },
        grid: {
          display: false,
          color: COLORS.gridLine,
          drawBorder: false,
        },
        border: {
          display: false,
        },
      },
    },
  };
}

// ============================================================================
// DATASET STYLING DEFAULTS
// ============================================================================

/**
 * Default styling for line chart datasets
 * - Smooth curves (tension 0.3 for dashboard, 0.4 for base)
 * - No point markers by default (better performance)
 * - No fill by default (pure line chart)
 */
export function getLineDatasetDefaults(variant: 'dashboard' | 'base' = 'base') {
  return {
    borderWidth: 2,
    tension: variant === 'dashboard' ? 0.3 : 0.4,
    fill: false,
    pointRadius: 0, // No points by default for better performance
    pointHoverRadius: 4,
    pointBorderWidth: 0,
  };
}

/**
 * Default styling for bar chart datasets
 * - No border
 * - No rounded corners
 * - Flexible bar thickness
 */
export function getBarDatasetDefaults() {
  return {
    borderWidth: 0,
    barThickness: 'flex' as const,
  };
}

// ============================================================================
// DATE FORMATTERS
// ============================================================================

/**
 * Format date string for X axis labels based on granularity
 * Uses native toLocaleDateString for month names
 */
function formatAxisLabel(dateStr: string, granularity: 'daily' | 'weekly' | 'monthly'): string {
  const date = new Date(dateStr);

  if (granularity === 'daily') {
    // Format: "7 Aug", "21 Sep"
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    return `${day} ${month}`;
  }

  if (granularity === 'weekly') {
    // Format: "19-25 May", "28-03 Jul"
    const endDate = new Date(date);
    endDate.setDate(endDate.getDate() + 6);
    const startDay = date.getDate();
    const endDay = endDate.getDate();
    const month = endDate.toLocaleDateString("en-US", { month: "short" });
    return `${startDay}-${endDay} ${month}`;
  }

  if (granularity === 'monthly') {
    // Format: "Jun 2025", "Jul 2025"
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${month} ${year}`;
  }

  return dateStr;
}

/**
 * Format date string for tooltip title based on granularity
 * Provides more detailed date information than axis labels
 */
function formatTooltipTitle(dateStr: string, granularity: 'daily' | 'weekly' | 'monthly'): string {
  const date = new Date(dateStr);

  if (granularity === 'daily') {
    // Format: "7 August 2025"
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  if (granularity === 'weekly') {
    // Format: "7-13 August 2025" or "28 July - 3 August 2025"
    const endDate = new Date(date);
    endDate.setDate(endDate.getDate() + 6);

    const startDay = date.getDate();
    const endDay = endDate.getDate();
    const startMonth = date.toLocaleDateString("en-US", { month: "long" });
    const endMonth = endDate.toLocaleDateString("en-US", { month: "long" });
    const year = endDate.getFullYear();

    if (startMonth === endMonth) {
      return `${startDay}-${endDay} ${startMonth} ${year}`;
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
    }
  }

  if (granularity === 'monthly') {
    // Format: "August 2025"
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const year = date.getFullYear();
    return `${month} ${year}`;
  }

  return dateStr;
}

// ============================================================================
// TIME SERIES CONFIGURATIONS
// ============================================================================

/**
 * Get time series specific chart configuration
 * Handles granularity-based axis and tooltip formatting
 *
 * @param granularity - Time period granularity (daily/weekly/monthly)
 * @param chartType - Chart type (bar/line) for stacking behavior
 */
export function getTimeSeriesConfig(
  granularity: 'daily' | 'weekly' | 'monthly',
  chartType: 'bar' | 'line'
): Partial<ChartOptions<any>> {
  return {
    scales: {
      x: {
        stacked: chartType === 'bar',
        ticks: {
          callback: function(value: any): string {
            // @ts-expect-error - Chart.js context typing - this.getLabelForValue exists at runtime
            const dateStr = this.getLabelForValue(value);
            return formatAxisLabel(dateStr, granularity);
          }
        }
      },
      y: {
        stacked: chartType === 'bar'
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: function(tooltipItems: any[]): string {
            if (tooltipItems.length === 0) return "";
            const dateStr = tooltipItems[0].label;
            return formatTooltipTitle(dateStr, granularity);
          }
        }
      }
    }
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Deep merge chart options
 * Allows overriding specific properties while keeping defaults
 */
export function mergeChartOptions(
  baseOptions: Partial<ChartOptions<any>>,
  overrides: Partial<ChartOptions<any>>
): ChartOptions<any> {
  // Helper function for deep merging objects
  function deepMerge(target: any, source: any): any {
    if (!source) return target;
    if (!target) return source;

    const result = { ...target };

    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = deepMerge(target[key] || {}, source[key]);
      } else if (source[key] !== undefined) {
        result[key] = source[key];
      }
    }

    return result;
  }

  // Deep merge scales
  const mergedScales = deepMerge(baseOptions.scales || {}, overrides.scales || {});

  // Deep merge plugins
  const mergedPlugins = deepMerge(baseOptions.plugins || {}, overrides.plugins || {});

  return {
    ...baseOptions,
    ...overrides,
    scales: mergedScales,
    plugins: mergedPlugins,
  } as ChartOptions<any>;
}
